import { useQuery } from '@tanstack/react-query';
import { api } from './api.ts';
import { useSession } from './session.ts';
import type { User } from './types.ts';

/** Active technicians and admins, for the assignment picker. Only fetched for IT accounts. */
export function useItUsers() {
  const { isIt } = useSession();
  return useQuery({
    enabled: isIt,
    queryKey: ['users', 'it'],
    queryFn: async () =>
      (await api.get<{ items: User[] }>('/api/users?role=it')).items.filter((u) => u.active),
    staleTime: 5 * 60_000,
  });
}

export interface QueueCounts {
  active: number;
  mine: number;
  unassigned: number;
  overdue: number;
  waiting: number;
  personal: number;
}

export function useQueueCounts() {
  return useQuery({
    // Under ['tickets'] so live ticket events refresh the counts too.
    queryKey: ['tickets', 'counts'],
    queryFn: () => api.get<QueueCounts>('/api/reports/queue-counts'),
  });
}

/** Trigger a file download from an API URL (keeps the session cookie). */
export async function download(url: string, fallbackName: string) {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.message ?? `Download failed (${res.status}).`);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const href = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), { href, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}
